<template>
  <div
    class="flex flex-col items-center justify-center w-full pb-20 bg-[#F2F6FA]"
  >
    <section
      class="flex flex-col items-center w-full justify-center xl:w-[1285px]"
    >
      <div
        class="flex flex-col items-center justify-center w-full h-full pb-20"
      >
        <section
          class="flex flex-col items-center w-full justify-center xl:w-[100%]"
        >
          <div
            class="w-full xl:w-[100%] flex justify-center items-center gap-4"
          >
            <div class="w-full bg-white rounded-xl">
              <div
                class="flex flex-col justify-between w-full px-5 py-4 mx-auto"
              >
                <!-- Header -->
                <div class="flex items-center justify-between mb-4">
                  <div class="flex items-center gap-2">
                    <v-btn
                      icon
                      @click="$router.back()"
                      class="mr-2"
                    >
                      <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <h2 class="text-xl font-semibold">Photo Target Setting</h2>
                  </div>
                </div>

                <!-- Toggle Switch -->
                <div class="border-b pb-4 mb-4">
                  <div class="flex items-center justify-between">
                    <div>
                      <span class="font-medium">Set Weekly Photo Target</span>
                      <p class="text-sm text-gray-500 mt-1">
                        Enable this to set weekly photo upload targets for your locations
                      </p>
                    </div>
                    <v-switch
                      v-model="weeklyPhotoTarget"
                      @change="updateWeeklyPhotoTarget"
                      color="primary"
                      :loading="isUpdating"
                    ></v-switch>
                  </div>
                </div>

                <!-- Search Bar and Locations - Only show when toggle is on -->
                <div v-if="weeklyPhotoTarget">
                  <!-- Apply to All Section -->
                  <div class="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div class="flex items-center justify-between gap-4">
                      <div class="flex-1">
                        <h4 class="font-medium text-gray-800 mb-1">Set Target for All Locations</h4>
                        <p class="text-sm text-gray-600">Apply the same weekly photo target to all locations</p>
                      </div>
                      <div class="flex items-center gap-2">
                        <v-text-field
                          v-model.number="applyToAllValue"
                          type="number"
                          min="0"
                          label="Target Count"
                          outlined
                          dense
                          hide-details
                          class="apply-all-input"
                          style="width: 150px"
                        ></v-text-field>
                        <v-btn
                          color="primary"
                          @click="applyToAllLocations"
                          :disabled="!applyToAllValue || applyToAllValue < 0"
                          :loading="isApplyingToAll"
                        >
                          Apply to All
                        </v-btn>
                      </div>
                    </div>
                  </div>

                  <!-- Search Bar -->
                  <div class="mb-4">
                    <v-text-field
                      v-model="searchQuery"
                      placeholder="Search locations..."
                      prepend-inner-icon="mdi-magnify"
                      outlined
                      dense
                      clearable
                      hide-details
                      @input="filterLocations"
                    ></v-text-field>
                  </div>

                  <!-- Loading State -->
                  <div v-if="isLoading" class="text-center py-8">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      :size="50"
                      :width="5"
                    ></v-progress-circular>
                    <p class="text-gray-600 mt-3">Loading locations...</p>
                  </div>

                  <!-- No Locations State -->
                  <div
                    v-else-if="!isLoading && stores.length === 0"
                    class="text-center py-8"
                  >
                    <v-icon size="50" color="grey">mdi-map-marker-off</v-icon>
                    <p class="text-gray-600 mt-3">No locations found</p>
                    <p class="text-sm text-gray-500 mt-1">
                      Please connect your Google Business Profile locations first
                    </p>
                  </div>

                  <!-- Locations List -->
                  <div
                    v-else-if="!isLoading && filteredStores.length > 0"
                    class="space-y-3"
                  >
                    <div
                      v-for="store in filteredStores"
                      :key="store.id"
                      class="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div class="flex items-start justify-between">
                        <div class="flex-1">
                          <h3 class="font-medium text-gray-900">
                            {{ store.title || store.name }}
                          </h3>
                          <p class="text-sm text-gray-500 mt-1">
                            <v-icon small class="mr-1">mdi-map-marker</v-icon>
                            {{ getStoreAddress(store) }}
                          </p>
                        </div>
                        <div class="ml-4 flex items-center gap-2">
                          <v-text-field
                            v-model.number="store.weeklyTarget"
                            type="number"
                            min="0"
                            label="Weekly Target"
                            outlined
                            dense
                            hide-details
                            :readonly="!isEditMode(store.id)"
                            :disabled="!isEditMode(store.id)"
                            class="weekly-target-input"
                            style="width: 150px"
                            @change="updateLocationTarget(store)"
                          ></v-text-field>
                          <v-btn
                            icon
                            small
                            @click="toggleEditMode(store)"
                            :color="isEditMode(store.id) ? 'success' : 'primary'"
                            :loading="savingStoreIds.includes(store.id)"
                          >
                            <v-icon small>
                              {{ isEditMode(store.id) ? 'mdi-check' : 'mdi-pencil' }}
                            </v-icon>
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- No Results State -->
                  <div
                    v-else-if="!isLoading && searchQuery && filteredStores.length === 0"
                    class="text-center py-8"
                  >
                    <v-icon size="50" color="grey">mdi-magnify-close</v-icon>
                    <p class="text-gray-600 mt-3">
                      No locations found matching "{{ searchQuery }}"
                    </p>
                    <v-btn
                      text
                      color="primary"
                      @click="clearSearch"
                      class="mt-2"
                    >
                      Clear Search
                    </v-btn>
                  </div>
                </div>

                <!-- Info message when toggle is off -->
                <div v-else class="text-center py-8 text-gray-500">
                  <v-icon size="50" color="grey">mdi-information-outline</v-icon>
                  <p class="mt-3">
                    Enable "Set Weekly Photo Target" to configure photo upload targets for your locations
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>
  </div>
</template>

<script>
import { doc, getDoc, updateDoc, collection, getDocs, query, where } from "firebase/firestore";
import { db } from "@/utils/firebase.utils";

export default {
  name: "PhotoTargetSetting",
  data() {
    return {
      weeklyPhotoTarget: false,
      isUpdating: false,
      isLoading: false,
      stores: [],
      filteredStores: [],
      searchQuery: "",
      locationTargets: {},
      invitedMembers: [], // Store all fetched members
      applyToAllValue: null, // Value for apply to all
      isApplyingToAll: false, // Loading state for apply to all
      editModeStoreIds: [], // Array of store IDs in edit mode
      savingStoreIds: [], // Array of store IDs being saved
    };
  },
  computed: {
    currentUID() {
      return this.$store.state.uid || this.$store.getters.currentUID || this.$store.getters.authUser?.uid;
    },
    WID() {
      return this.$store.state.wid || this.$store.getters.currentWID || this.$store.getters.WID;
    },
    globalStores() {
      return this.$store.state.stores || this.$store.getters.stores || this.$store.getters.globalStores || [];
    },
  },
  methods: {
    async fetchWeeklyPhotoTargetStatus() {
      try {
        // Validate required fields
        if (!this.currentUID || !this.WID) {
          console.warn("User ID or Workspace ID is not available yet");
          return;
        }
        
        const workspaceRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID
        );
        const workspaceDoc = await getDoc(workspaceRef);
        
        if (workspaceDoc.exists()) {
          const workspaceData = workspaceDoc.data();
          this.weeklyPhotoTarget = workspaceData.gmb_weekly_photo_target || false;
          this.locationTargets = workspaceData.location_photo_targets || {};
        }
      } catch (error) {
        console.error("Error fetching weekly photo target status:", error);
        this.weeklyPhotoTarget = false;
      }
    },

    async updateWeeklyPhotoTarget() {
      try {
        this.isUpdating = true;
        
        // Validate required fields
        if (!this.currentUID || !this.WID) {
          throw new Error("User ID or Workspace ID is missing");
        }
        
        const workspaceRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID
        );
        
        await updateDoc(workspaceRef, {
          gmb_weekly_photo_target: this.weeklyPhotoTarget,
        });
        
        this.$snackbar.show(
          `Weekly photo target ${
            this.weeklyPhotoTarget ? "enabled" : "disabled"
          } successfully`
        );
        
        if (this.weeklyPhotoTarget && this.stores.length === 0) {
          await this.fetchStores();
        }
      } catch (error) {
        console.error("Error updating weekly photo target:", error);
        this.$alert.show(`Failed to update weekly photo target: ${error.message}`);
        this.weeklyPhotoTarget = !this.weeklyPhotoTarget;
      } finally {
        this.isUpdating = false;
      }
    },

    async fetchStores() {
      try {
        this.isLoading = true;
        this.stores = [];
        this.invitedMembers = [];
        
        // Validate required fields
        if (!this.currentUID || !this.WID) {
          console.warn("User ID or Workspace ID is not available yet");
          return;
        }

        // Use a Set to track unique store IDs and prevent duplicates
        const addedStoreIds = new Set();

        // Method 1: Try to get from globalStores first
        if (this.globalStores && this.globalStores.length > 0) {
          console.log("Using globalStores data:", this.globalStores);
          this.globalStores.forEach((storeData) => {
            const storeId = storeData.id || storeData.wid;
            
            // Skip if already added
            if (addedStoreIds.has(storeId)) {
              return;
            }
            
            addedStoreIds.add(storeId);
            this.stores.push({
              ...storeData,
              id: storeId,
              wid: storeId,
              weeklyTarget: storeData.gmb_weekly_target_photo_cnt || this.locationTargets[storeId] || 0,
            });
          });
        }

        // Method 2: If no globalStores, fetch directly from Firestore
        if (this.stores.length === 0) {
          console.log("Fetching stores directly from Firestore");
          const workspacesRef = collection(db, "users", this.currentUID, "fly_workspaces");
          const workspacesQuery = query(
            workspacesRef,
            where("storeWorkspace", "==", true),
            where("admin", "==", this.WID)
          );
          
          const querySnapshot = await getDocs(workspacesQuery);
          
          querySnapshot.forEach((doc) => {
            const storeData = doc.data();
            const storeId = doc.id;
            
            // Skip if already added
            if (addedStoreIds.has(storeId)) {
              return;
            }
            
            addedStoreIds.add(storeId);
            
            // Add to invitedMembers for compatibility
            this.invitedMembers.push({
              ...storeData,
              id: storeId,
              wid: storeId,
            });
            
            // Add to stores with weekly target
            this.stores.push({
              ...storeData,
              id: storeId,
              wid: storeId,
              title: storeData.title || storeData.name || storeData.storeLocation,
              name: storeData.name || storeData.title || storeData.storeLocation,
              weeklyTarget: storeData.gmb_weekly_target_photo_cnt || this.locationTargets[storeId] || 0,
              // Include address fields for display
              address: storeData.address || storeData.storeLocation,
              sub_locality: storeData.sub_locality,
              city: storeData.city,
              state: storeData.state,
              zipcode: storeData.zipcode,
              gmbProfile: storeData.gmbProfile,
            });
          });
        }

        // Method 3: If still no stores, try fetching all workspaces (not just storeWorkspace)
        if (this.stores.length === 0) {
          console.log("Fetching all workspaces as fallback");
          const allWorkspacesRef = collection(db, "users", this.currentUID, "fly_workspaces");
          const allWorkspacesQuery = query(allWorkspacesRef, where("admin", "==", this.WID));
          
          const querySnapshot = await getDocs(allWorkspacesQuery);
          
          querySnapshot.forEach((doc) => {
            const storeData = doc.data();
            const storeId = doc.id;
            
            // Skip the default workspace or already added stores
            if (storeData.name === "default" || storeId === this.WID || addedStoreIds.has(storeId)) {
              return;
            }
            
            addedStoreIds.add(storeId);
            
            this.stores.push({
              ...storeData,
              id: storeId,
              wid: storeId,
              title: storeData.title || storeData.name || storeData.storeLocation || "Unnamed Location",
              name: storeData.name || storeData.title || storeData.storeLocation || "Unnamed Location",
              weeklyTarget: storeData.gmb_weekly_target_photo_cnt || this.locationTargets[storeId] || 0,
              address: storeData.address || storeData.storeLocation || "",
              sub_locality: storeData.sub_locality,
              city: storeData.city,
              state: storeData.state,
              zipcode: storeData.zipcode,
              gmbProfile: storeData.gmbProfile,
            });
          });
        }
        
        console.log("Total stores fetched:", this.stores.length);
        this.filteredStores = [...this.stores];
      } catch (error) {
        console.error("Error fetching stores:", error);
        this.$alert.show("Failed to load locations");
      } finally {
        this.isLoading = false;
      }
    },

    filterLocations() {
      if (!this.searchQuery) {
        this.filteredStores = [...this.stores];
        return;
      }
      
      const searchTerm = this.searchQuery.toLowerCase();
      this.filteredStores = this.stores.filter((store) => {
        const title = (store.title || store.name || "").toLowerCase();
        const address = this.getStoreAddress(store).toLowerCase();
        return title.includes(searchTerm) || address.includes(searchTerm);
      });
    },

    clearSearch() {
      this.searchQuery = "";
      this.filteredStores = [...this.stores];
    },

    getStoreAddress(store) {
      // Try to get address from different possible fields
      if (store?.gmbProfile?.storefrontAddress) {
        const addr = store.gmbProfile.storefrontAddress;
        const addressParts = [];
        
        if (addr.addressLines && addr.addressLines.length)
          addressParts.push(...addr.addressLines);
        if (addr.locality) addressParts.push(addr.locality);
        if (addr.administrativeArea)
          addressParts.push(addr.administrativeArea);
        if (addr.postalCode) addressParts.push(addr.postalCode);
        
        return addressParts.join(", ");
      } else if (store?.address) {
        return store.address;
      } else if (store?.sub_locality || store?.city) {
        const parts = [];
        if (store.sub_locality) parts.push(store.sub_locality);
        if (store.city) parts.push(store.city);
        return parts.join(", ");
      }
      return "Address not available";
    },

    async updateLocationTarget(store) {
      // Only save if in edit mode and clicking the check button
      if (!this.isEditMode(store.id)) {
        return;
      }
      
      try {
        // Add to saving list
        this.savingStoreIds.push(store.id);
        
        // Validate required fields
        if (!this.currentUID || !this.WID) {
          throw new Error("User ID or Workspace ID is missing");
        }
        
        // Update the individual workspace document with gmb_weekly_target_photo_cnt
        const storeWorkspaceRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          store.id
        );
        
        await updateDoc(storeWorkspaceRef, {
          gmb_weekly_target_photo_cnt: store.weeklyTarget || 0,
        });
        
        // Also update in the main workspace's location_photo_targets for backward compatibility
        const workspaceRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID
        );
        
        const workspaceDoc = await getDoc(workspaceRef);
        let currentTargets = {};
        
        if (workspaceDoc.exists()) {
          currentTargets = workspaceDoc.data().location_photo_targets || {};
        }
        
        currentTargets[store.id] = store.weeklyTarget || 0;
        
        await updateDoc(workspaceRef, {
          location_photo_targets: currentTargets,
        });
        
        // Remove from edit mode
        this.editModeStoreIds = this.editModeStoreIds.filter(id => id !== store.id);
        
        this.$snackbar.show(`Target updated for ${store.title || store.name}`);
      } catch (error) {
        console.error("Error updating location target:", error);
        this.$alert.show(`Failed to update location target: ${error.message}`);
      } finally {
        // Remove from saving list
        this.savingStoreIds = this.savingStoreIds.filter(id => id !== store.id);
      }
    },

    isEditMode(storeId) {
      return this.editModeStoreIds.includes(storeId);
    },

    toggleEditMode(store) {
      if (this.isEditMode(store.id)) {
        // If in edit mode, save the changes
        this.updateLocationTarget(store);
      } else {
        // Enter edit mode
        this.editModeStoreIds.push(store.id);
      }
    },

    async applyToAllLocations() {
      if (!this.applyToAllValue || this.applyToAllValue < 0) {
        this.$alert.show("Please enter a valid target count");
        return;
      }
      
      try {
        this.isApplyingToAll = true;
        
        // Validate required fields
        if (!this.currentUID || !this.WID) {
          throw new Error("User ID or Workspace ID is missing");
        }
        
        // Update all stores with the new target value
        const updatePromises = [];
        const updatedTargets = {};
        
        for (const store of this.stores) {
          // Update the individual workspace document
          const storeWorkspaceRef = doc(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            store.id
          );
          
          updatePromises.push(
            updateDoc(storeWorkspaceRef, {
              gmb_weekly_target_photo_cnt: this.applyToAllValue,
            })
          );
          
          // Update local state
          store.weeklyTarget = this.applyToAllValue;
          updatedTargets[store.id] = this.applyToAllValue;
        }
        
        // Execute all updates in parallel
        await Promise.all(updatePromises);
        
        // Also update the main workspace's location_photo_targets
        const workspaceRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID
        );
        
        await updateDoc(workspaceRef, {
          location_photo_targets: updatedTargets,
        });
        
        // Update filtered stores to reflect changes
        this.filteredStores = [...this.stores];
        
        this.$snackbar.show(`Target of ${this.applyToAllValue} applied to all ${this.stores.length} locations`);
        
        // Clear the apply to all value
        this.applyToAllValue = null;
      } catch (error) {
        console.error("Error applying target to all locations:", error);
        this.$alert.show(`Failed to apply target to all locations: ${error.message}`);
      } finally {
        this.isApplyingToAll = false;
      }
    },
  },
  async mounted() {
    // Wait for Vuex store to be fully initialized
    let retryCount = 0;
    const maxRetries = 10;
    
    while ((!this.currentUID || !this.WID) && retryCount < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 200));
      retryCount++;
    }
    
    if (!this.currentUID || !this.WID) {
      console.error("Failed to get User ID or Workspace ID after retries");
      this.$alert.show("Failed to load user information. Please refresh the page.");
      return;
    }
    
    console.log("Component mounted with UID:", this.currentUID, "WID:", this.WID);
    
    await this.fetchWeeklyPhotoTargetStatus();
    if (this.weeklyPhotoTarget) {
      await this.fetchStores();
    }
  },
  watch: {
    globalStores: {
      immediate: false,
      handler(newStores, oldStores) {
        // Only fetch if there's actually new data and we haven't fetched yet
        const hasNewData = newStores && newStores.length > 0;
        const isDataChanged = JSON.stringify(newStores) !== JSON.stringify(oldStores);
        
        if (hasNewData && isDataChanged && this.weeklyPhotoTarget && !this.isLoading) {
          console.log("GlobalStores updated with new data:", newStores?.length || 0, "stores");
          // Only fetch if we don't have stores yet or if the data actually changed
          if (this.stores.length === 0 || (oldStores && oldStores.length !== newStores.length)) {
            this.fetchStores();
          }
        }
      },
    },
    currentUID: {
      handler(newVal, oldVal) {
        // Only trigger if value actually changed and we have all required data
        if (newVal && newVal !== oldVal && this.WID && this.weeklyPhotoTarget && this.stores.length === 0 && !this.isLoading) {
          this.fetchStores();
        }
      },
    },
    WID: {
      handler(newVal, oldVal) {
        // Only trigger if value actually changed and we have all required data
        if (newVal && newVal !== oldVal && this.currentUID && this.weeklyPhotoTarget && this.stores.length === 0 && !this.isLoading) {
          this.fetchStores();
        }
      },
    },
  },
};
</script>

<style scoped>
.space-y-3 > * + * {
  margin-top: 0.75rem;
}

.weekly-target-input >>> .v-input__control {
  min-height: 38px !important;
}

.weekly-target-input >>> .v-text-field input {
  padding: 8px 12px !important;
}

.apply-all-input >>> .v-input__control {
  min-height: 38px !important;
}

.apply-all-input >>> .v-text-field input {
  padding: 8px 12px !important;
}

/* Disabled state styling */
.weekly-target-input >>> .v-text-field--disabled input {
  color: rgba(0, 0, 0, 0.6) !important;
  background-color: #f5f5f5 !important;
}

/* Readonly state styling */
.weekly-target-input >>> .v-text-field input[readonly] {
  background-color: #fafafa !important;
}
</style>